import { Controller } from "@hotwired/stimulus"

// Which browser a "Save from anywhere" page leads with, decided in the
// browser: the visitor's own pick (localStorage) wins, then
// navigator.userAgentData with a user-agent fallback. The server renders
// every state; this sets data-state on the element and the stylesheet shows
// one. Also names the key the extension page makes, "Chrome on macOS".
export default class PlatformController extends Controller {
  static values = { key: String, choices: Array }
  static targets = ["keyName", "browserSentence", "title"]

  connect() {
    this.family = this.detect()
    this.show(this.stored() ?? this.family)
  }

  // The pressed button is hidden by the new state, so focus moves to the
  // title, which reads out the state it switched to.
  pick({ params: { state } }) {
    this.store(state)
    this.show(state)
    this.titleTarget.focus()
  }

  keyNameTargetConnected(input) {
    input.value = this.keyName()
  }

  show(state) {
    this.state = state
    this.element.dataset.state = state
    if (this.hasKeyNameTarget) this.keyNameTarget.value = this.keyName()
    const name = this.family === "none" && this.browserName()
    if (this.hasBrowserSentenceTarget && name) this.browserSentenceTarget.textContent = `There is no ${name} extension yet.`
  }

  keyName() {
    if (this.state === "none") return "Browser extension"
    const browser = this.state === "firefox" ? "Firefox" : (this.family === "chrome" && this.browserName()) || "Chrome"
    const os = this.osName()
    return os ? `${browser} on ${os}` : browser
  }

  // A phone or tablet never gets a state with an extension: the extension
  // runs on a computer only, whatever the browser calls itself.
  detect() {
    if (this.mobile) return "none"
    if (this.brands.some((b) => /Chromium|Chrome|Edge|Brave|Opera/.test(b)) || /Chrome\/|Chromium\//.test(this.ua)) return "chrome"
    if (/Firefox\//.test(this.ua)) return "firefox"
    return "none"
  }

  browserName() {
    if (this.mobile) return ""
    for (const [pattern, name] of [[/Microsoft Edge/, "Edge"], [/Brave/, "Brave"], [/Opera/, "Opera"], [/Chrome/, "Chrome"]]) {
      if (this.brands.some((b) => pattern.test(b))) return name
    }
    if (/Edg\//.test(this.ua)) return "Edge"
    if (/OPR\//.test(this.ua)) return "Opera"
    if (/Chrome\//.test(this.ua)) return "Chrome"
    if (/Firefox\//.test(this.ua)) return "Firefox"
    if (/Safari\//.test(this.ua)) return "Safari"
    return ""
  }

  osName() {
    const platform = navigator.userAgentData?.platform || ""
    if (/mac/i.test(platform) || /Macintosh/.test(this.ua)) return "macOS"
    if (/win/i.test(platform) || /Windows/.test(this.ua)) return "Windows"
    if (/chrome ?os/i.test(platform) || /CrOS/.test(this.ua)) return "Chrome OS"
    if (/linux/i.test(platform) || /Linux/.test(this.ua)) return "Linux"
    return ""
  }

  get ua() {
    return navigator.userAgent
  }

  get brands() {
    return (navigator.userAgentData?.brands || []).map((b) => b.brand)
  }

  // A tablet's client hints say mobile: false; Android shows in the platform
  // and the user agent instead.
  get mobile() {
    return Boolean(navigator.userAgentData?.mobile) ||
      /android/i.test(navigator.userAgentData?.platform || "") ||
      /Android|iPhone|iPad|iPod/.test(this.ua)
  }

  stored() {
    try {
      const state = localStorage.getItem(this.keyValue)
      return this.choicesValue.includes(state) ? state : null
    } catch {
      return null
    }
  }

  store(state) {
    try {
      localStorage.setItem(this.keyValue, state)
    } catch {
      // Private mode or blocked storage: the pick lasts for the page.
    }
  }
}
